import { useEffect, useState } from 'react';
import { TextField, CircularProgress, Typography } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import {
  createScaffolderFieldExtension,
  FieldExtensionComponentProps,
} from '@backstage/plugin-scaffolder-react';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';

const KNOWN_ENDPOINTS = ['quality-gates', 'permission-templates', 'groups'] as const;

interface SonarQubeOptionPickerUiOptions {
  endpoint: (typeof KNOWN_ENDPOINTS)[number];
  /** Used as the sole option (and auto-selected if empty) when the live fetch fails. */
  fallback?: string;
  /** Pre-selected when the field is empty and this value is present in the live-fetched options. Still changeable via the dropdown. */
  defaultOption?: string;
  [key: string]: string | number | boolean | object | any[] | null | undefined;
}

const SonarQubeOptionPickerComponent = ({
  onChange,
  rawErrors,
  formData,
  required,
  disabled,
  schema,
  uiSchema,
}: FieldExtensionComponentProps<string, SonarQubeOptionPickerUiOptions>) => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const endpoint = uiSchema['ui:options']?.endpoint;
  const fallback = uiSchema['ui:options']?.fallback;
  const defaultOption = uiSchema['ui:options']?.defaultOption;
  const [options, setOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [usingFallback, setUsingFallback] = useState(false);

  useEffect(() => {
    if (!endpoint) return undefined;
    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        const baseUrl = await discoveryApi.getBaseUrl('sonarqube-directory');
        const response = await fetchApi.fetch(`${baseUrl}/${endpoint}`);
        if (!response.ok) {
          throw new Error(`${response.status} ${response.statusText}`);
        }
        const values: string[] = await response.json();
        if (cancelled) return;
        setOptions(values);
        setUsingFallback(false);
        if (defaultOption && !formData && values.includes(defaultOption)) {
          onChange(defaultOption);
        }
      } catch (e) {
        if (cancelled) return;
        setOptions(fallback ? [fallback] : []);
        setUsingFallback(true);
        if (fallback && !formData) {
          onChange(fallback);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discoveryApi, fetchApi, endpoint, fallback, defaultOption]);

  if (!endpoint || !KNOWN_ENDPOINTS.includes(endpoint)) {
    return (
      <Typography color="error">
        SonarQubeOptionPicker: unknown ui:options.endpoint "{endpoint}"
      </Typography>
    );
  }

  const unavailable = usingFallback && !fallback;
  const helperText = () => {
    if (rawErrors?.length) return rawErrors[0];
    if (unavailable) return 'Not available right now — add individual users instead.';
    if (usingFallback) {
      return 'Could not load live options from SonarQube — showing default only.';
    }
    return schema.description;
  };

  return (
    <Autocomplete
      disabled={disabled || unavailable}
      loading={loading}
      options={options}
      value={formData ?? null}
      onChange={(_, value: string | null) => onChange(value ?? undefined)}
      renderInput={params => (
        <TextField
          {...params}
          label={schema.title}
          required={required}
          error={!!rawErrors?.length}
          helperText={helperText()}
          margin="dense"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export const SonarQubeOptionPickerFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension<string, SonarQubeOptionPickerUiOptions>({
    name: 'SonarQubeOptionPicker',
    component: SonarQubeOptionPickerComponent,
  }),
);
